import { useState, createContext, useRef, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';

import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';

import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import 'styles/MapProvider.css';

import { ALL_PICTURE_FIELDS } from 'graphql/APICalls';



// TODO: make Mapbox API key fetched from backend
const MAPBOX_API_KEY = 'pk.eyJ1IjoiZ3V5ZnJvbXRoZXJlIiwiYSI6ImNtNXVyc2wzbTExeW4ybXBhczlxNGMzdnUifQ.OZhNfwfOrhBz8lFn9j3BkA';





function createCustomMarker(imageURL, zIndex) {
  const marker = document.createElement('div');
  Object.assign(marker.style, {backgroundImage: `url(${imageURL})`, zIndex});
  marker.className = 'custom-map-marker';
  return marker};

function popupHTML(activity){
  return `
    <div class="activity-popup">
      <div class="title" onClick="window.handleNavigate('/t/${activity.shortId}'); return false;">
        <b>${activity.title}</b>
      </div>
      <div class="creator" onClick="window.handleNavigate('/u/${activity.creator.username}'); return false;">
        with <i><b>${activity.creator.name}</b></i>
      </div>
      <div class="location">
        at <i>${activity.location.name}</i>
      </div>
    </div>
  `
}



function getActivityMarker(activity) {
  const marker = new mapboxgl.Marker(createCustomMarker(activity?.creator?.picture?.imageSrc))
    .setLngLat(activity.location.geometry.coordinates);
  marker.activityId = activity.id;
  marker.getElement().style.display = 'var(--map-marker-display)';
  const popup = new mapboxgl.Popup({closeButton: false, focusAfterOpen: false, offset: 17}).setHTML(popupHTML(activity));
  marker.setPopup(popup);

  return marker;
};
  

const LOCATION_ESTIMATE_QUERY = gql`
  query { locationEstimate { name, geometry { coordinates } } }
  `;


// Create context for MapProvider's children, giving them access to the map object
export const MapContext = createContext(null);


// Resize the map when the container is resized, e.g. on orientation change
function useMapResize(map, containerRef) {
  useEffect(() => {
    if (!map?.current || !containerRef?.current) return;

    const observer = new ResizeObserver(() => {
      console.log('Resizing map');
      map.current.resize();
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [map, containerRef]);
}



export default function MapProvider({children}){
  
  const navigate = useNavigate();
  window.handleNavigate = path => navigate(path);

  const mapContainer = useRef(null);
  const map = useRef(null);

  const [desiredView, setDesiredView] = useState({center: [30, 48], zoom: 1});

  const [currentlySelectedActivityId, setCurrentlySelectedActivityId] = useState(null);

  useEffect(() => {
    if (map.current && desiredView?.center && desiredView?.zoom) {
      map.current.easeTo({...desiredView, duration: 1000});
    }
  }, [desiredView]);
  

  useQuery(LOCATION_ESTIMATE_QUERY,
    {onCompleted: data => {
        setDesiredView({center: data.locationEstimate.geometry.coordinates, zoom: 7});
        setSelectedLocation(data.locationEstimate.geometry.coordinates)
    }}
  );



  const [boxBounds, setBoxBounds] = useState({minLat: 0, maxLat: 0, minLng: 0, maxLng: 0});

  const handleMoveEnd = () => {
    const bounds = map.current.getBounds(); 
    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();
    const box = {
      minLat: southWest.lat,
      minLng: southWest.lng,
      maxLat: northEast.lat,
      maxLng: northEast.lng
    };
    setBoxBounds(box);
  };

  const [timeWindow, setTimeWindow] = useState(null);

  const ACTIVITIES_IN_BOX_QUERY = gql`
    query ($box: LngLatBoundsInput!, $timeWindow: TimeWindowInput){
      getActivitiesInBox(box: $box, timeWindow: $timeWindow)
        {id, shortId, title, description, location {name, geometry { coordinates }},
        creator {id, name, username, email, picture ${ALL_PICTURE_FIELDS}}, createdAt, userRole}
    }
  `;

  const activitiesInBoxQuery = useQuery(ACTIVITIES_IN_BOX_QUERY, {
    variables: {box: boxBounds, timeWindow},
    fetchPolicy: 'network-only'
  });

  const updateLocation = useCallback(()=>{activitiesInBoxQuery.refetch({fetchPolicy: 'network-only'})}, []);


  
  useEffect(() => {
    if (Array.isArray(activitiesInBoxQuery?.data?.getActivitiesInBox)){
      setVisibleActivities(activitiesInBoxQuery.data.getActivitiesInBox) }
  }, [activitiesInBoxQuery]);





  const [showActivityMarkers, setShowActivityMarkers] = useState(true);

  useEffect(()=>{
    document.documentElement.style.setProperty('--map-marker-display',
      showActivityMarkers ? 'block' : 'none'
    )
  }, [showActivityMarkers]);


  const [visibleActivities, setVisibleActivities] = useState([]);
  useEffect(() => {
    setActivityMarkers(visibleActivities.map(activity => getActivityMarker(activity)));
  }, [visibleActivities]);


  const [activityMarkers, setActivityMarkers] = useState([]);

  useEffect(()=>{
    activityMarkers.forEach(marker => {
      const popup = marker.getPopup();
      popup.on('open' , () => { setCurrentlySelectedActivityId(marker.activityId) });
      popup.on('close', () => { setCurrentlySelectedActivityId(null) });
      marker.addTo(map.current);
      if (marker.activityId === currentlySelectedActivityId) marker.togglePopup()
    });
    return () => {
      activityMarkers.forEach(marker => marker.remove());
    }
  }, [activityMarkers]);

  
  const activityMarkersRef = useRef(activityMarkers);
  useEffect(()=>{activityMarkersRef.current = activityMarkers}, [activityMarkers]);



  const [showGeocoder, setShowGeocoder] = useState(true);
  const geocoderControl = useRef(new MapboxGeocoder({ mapboxgl, accessToken: MAPBOX_API_KEY, marker: false, clearOnBlur: true, placeholder: 'Location' }));

  useEffect(() => {
    if (map.current) {
      if ( map.current.hasControl(geocoderControl.current) && !showGeocoder)
        map.current.removeControl(geocoderControl.current);
      if (!map.current.hasControl(geocoderControl.current) &&  showGeocoder)
        map.current.addControl   (geocoderControl.current, 'top-left');
    }
  }, [showGeocoder]);


  
  useEffect(()=>{

    if (map.current) return
    mapboxgl.accessToken = MAPBOX_API_KEY;
    map.current = new mapboxgl.Map({ container: mapContainer.current,
                                    //style: 'mapbox://styles/mapbox/streets-v12',
                                    //style: 'mapbox://styles/guyfromthere/cmb1pr2lk00gc01scgts32gn4',
                                    style: 'mapbox://styles/guyfromthere/cmbi20vfh00bg01smay779g6n',
                                    center: desiredView?.center, zoom: desiredView?.zoom, projection: 'globe' });
    
    console.log('New map created.');

    // Start the globe spinning
    map.current.easeTo({center: [-150, 48], duration: 300000, easing: t => t});

    map.current.on('moveend', handleMoveEnd);
    map.current.dragPan.enable({ deceleration: 1000, maxSpeed: 15000, linearity: 1 });

    if (showGeocoder) map.current.addControl(geocoderControl.current, 'top-left');

    document.addEventListener('click',
      clickEvent => {
        if (!mapContainer.current.contains(clickEvent.target)) {
          activityMarkersRef.current.forEach(marker => marker.getPopup().remove())
        }
      }
    );

    map.current.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
        showUserHeading: true
      })
    );
                                    
    return () => map.current.remove();
  }, []);

  useMapResize(map, mapContainer);

  const [locationSelector, setLocationSelector] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState([0,0]);
  const selectedLocationMarker = useRef(new mapboxgl.Marker());
  
  const setSelectedLocationToClickLocation = useCallback((e) => { setSelectedLocation([e.lngLat.lng, e.lngLat.lat]) }, [setSelectedLocation]);
  
  useEffect(()=>{

    if (locationSelector) {
      map.current.on ('click', setSelectedLocationToClickLocation);
      selectedLocationMarker.current.addTo(map.current) }

    else {
      map.current.off('click', setSelectedLocationToClickLocation);
      selectedLocationMarker.current.remove() }

    return ()=>{
      map.current.off('click', setSelectedLocationToClickLocation);
      selectedLocationMarker.current.remove() }

  }, [locationSelector]);
  
  useEffect(()=>{
    selectedLocationMarker.current.setLngLat(selectedLocation);
    map.selectedLocation = selectedLocation
  }, [selectedLocation]);

  const [indicatedActivity, setIndicatedActivity] = useState(null);
  const indicatedActivityMarker = useRef(null);

  useEffect(() => {
    if (indicatedActivity) {
      indicatedActivityMarker.current = getActivityMarker(indicatedActivity);
      indicatedActivityMarker.current.getElement().style.display = null;
      indicatedActivityMarker.current.getElement().style.cursor = 'grab';
      indicatedActivityMarker.current.setPopup(null);
      indicatedActivityMarker.current.addTo(map.current);
    }

    return () => {
      if (indicatedActivityMarker.current){
        indicatedActivityMarker.current.remove()
      }
    }
  }, [indicatedActivity]);

  map.adjust = (options) => {
    if (options.locationSelector  !== undefined) { setLocationSelector   (options.locationSelector) }
    if (options.activityMarkers   !== undefined) { setShowActivityMarkers(options.activityMarkers) }
    if (options.geocoder          !== undefined) { setShowGeocoder       (options.geocoder) }
    if (options.indicatedActivity !== undefined) { setIndicatedActivity  (options.indicatedActivity) }
    if (options.view              !== undefined) { setDesiredView        (options.view) }
    if (options.timeWindow        !== undefined) { setTimeWindow         (options.timeWindow) }
    if (options.center            !== undefined) { throw new Error('center is no longer supported, use view instead') }
    if (options.zoom              !== undefined) { throw new Error('zoom is no longer supported, use view instead') }
  }

  map.loan = (targetElementId, options) => {
    if (!map.previousViews) { map.previousViews = {} }

    return () => {

      map.adjust({...map.previousViews[targetElementId], ...options});

      activityMarkersRef.current.forEach(marker => marker.getPopup().remove())

      document.getElementById(targetElementId).appendChild(mapContainer.current);
      if (map.current) {map.current.resize()}

      return ()=>{
        document.getElementById('map_parking').appendChild(mapContainer.current);
        map.previousViews[targetElementId] = {
          view: {
            center: map.current.getCenter(),
            zoom: map.current.getZoom()
          }
        };
      }
    }
  }


  
  return (
    <>
      <MapContext.Provider value={{map, updateLocation, visibleActivities}}>
        {children}
      </MapContext.Provider>
      <div id="map_parking" style={{ display: 'none' }}>
        <div id="map_container" ref={mapContainer} className="map-container"/>
      </div>
    </>
  )
}

export function MapView({id, options}){
  const { map } = useContext(MapContext);
  useEffect( map.loan(id, options), []);
  useEffect( () => { map.adjust(options) }, [options]);
  return <div id={id} style={{display: "contents"}}/>
}